import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Filter, Loader2, Layers, FileCode, CheckSquare, Building2, Users, GitBranch, User, AlertTriangle, ArrowLeft } from 'lucide-react';
import DOMPurify from 'dompurify';
import type { AppDispatch, RootState } from '@/store';
import { globalSearch, selectSearchResults, selectSearchLoading, selectSearchFacets, setSelectedTypes } from '@/store/slices/searchSlice';
import { GlassCard, GlassPageHeader, GlassBadge, GlassButton } from '@/design-system';

// Entity type icons mapping
const entityIcons: Record<string, React.ElementType> = {
  Epic: FileCode,
  Feature: CheckSquare,
  Program: Building2,
  Team: Users,
  InvestibleStructure: Layers,
  Dependency: GitBranch,
  Resource: User,
  ResourceIntakeRequest: AlertTriangle,
};

// Entity type to route mapping
const entityRoutes: Record<string, string> = {
  Epic: '/epics',
  Feature: '/features',
  Program: '/programs',
  Team: '/teams',
  InvestibleStructure: '/investible-structures',
  Dependency: '/dependencies',
  Resource: '/resources',
  ResourceIntakeRequest: '/resource-intake-requests',
};

// Entity colors for badges
const entityColors: Record<string, string> = {
  Epic: 'blue',
  Feature: 'green',
  Program: 'purple',
  Team: 'orange',
  InvestibleStructure: 'cyan',
  Dependency: 'amber',
  Resource: 'pink',
  ResourceIntakeRequest: 'rose',
};

const ALL_ENTITY_TYPES = ['Epic', 'Feature', 'Program', 'Team', 'InvestibleStructure', 'Dependency', 'Resource', 'ResourceIntakeRequest'];

const SearchResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const results = useSelector(selectSearchResults);
  const isLoading = useSelector(selectSearchLoading);
  const facets = useSelector(selectSearchFacets);
  const selectedTypes = useSelector((state: RootState) => state.search.selectedTypes);

  const query = searchParams.get('q') || '';
  const [localQuery, setLocalQuery] = useState(query);

  // Perform search when query or types change
  useEffect(() => {
    if (query.trim()) {
      dispatch(globalSearch({
        query,
        types: selectedTypes.length > 0 ? selectedTypes : undefined,
        limit: 100
      }));
    }
  }, [query, selectedTypes, dispatch]);

  // Update local query when URL changes
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localQuery.trim()) {
      setSearchParams({ q: localQuery });
    }
  };

  // Handle type filter toggle
  const handleTypeToggle = (type: string) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    dispatch(setSelectedTypes(newTypes));
  };

  // Handle result click
  const handleResultClick = (result: any) => {
    const route = entityRoutes[result.entityType];
    if (route) {
      navigate(`${route}?highlight=${result.id}`);
    }
  };

  // Render highlight with mark tags (sanitized to prevent XSS)
  const renderHighlight = (text: string | undefined, highlights: Record<string, string[]> | undefined, field: string) => {
    if (highlights && highlights[field]?.[0]) {
      const sanitized = DOMPurify.sanitize(highlights[field][0], { ALLOWED_TAGS: ['mark', 'em', 'strong'] });
      return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
    }
    return text || '';
  };

  // Group results by entity type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.entityType]) {
      acc[result.entityType] = [];
    }
    acc[result.entityType].push(result);
    return acc;
  }, {} as Record<string, typeof results>);

  return (
    <div className="min-h-screen">
      <GlassPageHeader
        title="Search Results"
        subtitle={query ? `Results for "${query}"` : 'Enter a search query'}
        actions={
          <GlassButton variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </GlassButton>
        }
      />

      <div className="px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar Filters */}
          <div className="w-64 flex-shrink-0">
            <GlassCard className="p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter by Type
              </h3>
              <div className="space-y-2">
                {ALL_ENTITY_TYPES.map(type => {
                  const Icon = entityIcons[type];
                  const count = facets[type] || 0;
                  const isChecked = selectedTypes.length === 0 || selectedTypes.includes(type);

                  return (
                    <label
                      key={type}
                      className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-surface-muted/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked && selectedTypes.length > 0}
                        onChange={() => handleTypeToggle(type)}
                        className="rounded border-border text-accent-blue focus:ring-accent-blue/50"
                      />
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1 text-sm text-muted-foreground">{type}s</span>
                      <span className="text-xs text-muted">{count}</span>
                    </label>
                  );
                })}
              </div>

              {selectedTypes.length > 0 && (
                <button
                  onClick={() => dispatch(setSelectedTypes([]))}
                  className="mt-4 w-full text-sm text-accent-blue hover:text-accent-blue transition-colors"
                >
                  Clear filters
                </button>
              )}
            </GlassCard>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search Box */}
            <GlassCard className="p-4 mb-6">
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={localQuery}
                    onChange={(e) => setLocalQuery(e.target.value)}
                    placeholder="Search epics, features, programs, teams..."
                    className="w-full pl-10 pr-4 py-3 bg-surface-muted/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                  />
                </div>
                <GlassButton type="submit" variant="primary">
                  Search
                </GlassButton>
              </form>
            </GlassCard>

            {/* Results */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-accent-blue animate-spin" />
                <span className="ml-3 text-muted-foreground">Searching...</span>
              </div>
            ) : results.length === 0 ? (
              <GlassCard className="p-12 text-center">
                <Search className="w-12 h-12 text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {query ? 'No results found' : 'Start your search'}
                </h3>
                <p className="text-muted-foreground">
                  {query ? `No results matched "${query}". Try different keywords.` : 'Enter a search term above to find EPICs, Features, Programs, and more.'}
                </p>
              </GlassCard>
            ) : (
              <div className="space-y-4">
                {/* Results summary */}
                <div className="text-sm text-muted-foreground mb-4">
                  Found {results.length} results
                </div>

                {/* Results grouped by type */}
                {Object.entries(groupedResults).map(([entityType, typeResults]) => {
                  const Icon = entityIcons[entityType];

                  return (
                    <div key={entityType}>
                      {/* Entity type header */}
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <h3 className="text-sm font-semibold text-foreground">{entityType}s</h3>
                        <span className="text-xs text-muted">({typeResults.length})</span>
                      </div>

                      {/* Results */}
                      <div className="space-y-2 mb-6">
                        {typeResults.map(result => (
                          <GlassCard
                            key={result.id}
                            className="p-4 cursor-pointer hover:bg-surface-muted/50 transition-colors"
                            onClick={() => handleResultClick(result)}
                          >
                            <div className="flex items-start gap-4">
                              <div className={`p-2 rounded-lg bg-${entityColors[entityType]}-500/20`}>
                                <Icon className={`w-5 h-5 text-${entityColors[entityType]}-400`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-base font-medium text-foreground">
                                    {renderHighlight(result.title, result.highlights, 'title')}
                                  </h4>
                                  {result.metadata?.status && (
                                    <GlassBadge variant={result.metadata.status === 'ACTIVE' ? 'success' : 'default'}>
                                      {result.metadata.status}
                                    </GlassBadge>
                                  )}
                                </div>
                                {result.subtitle && (
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {result.subtitle}
                                  </p>
                                )}
                                {result.highlights?.description?.[0] && (
                                  <p className="text-sm text-muted line-clamp-2">
                                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.highlights.description[0], { ALLOWED_TAGS: ['mark', 'em', 'strong'] }) }} />
                                  </p>
                                )}
                              </div>
                              <div className="text-xs text-muted">
                                Score: {Math.round(result.score)}
                              </div>
                            </div>
                          </GlassCard>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Highlight styles */}
      <style>{`
        mark {
          background-color: var(--accent-blue-soft, rgba(59, 130, 246, 0.3));
          color: var(--text-primary, white);
          padding: 0 2px;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};

export default SearchResultsPage;
